import {
  getSMTPSettings,
  createSMTPSettings,
  updateSMTPSettings,
  deleteSMTPSettings,
} from '@/services/SMTPSettingsService'

const mapApiItemToView = (item) => ({
  id: item?.id || null,
  name: item?.name || '',
  consumerId: item?.consumer_id || null,
  mailer: item?.mail_mailer || 'smtp',
  host: item?.mail_host || '',
  port: Number(item?.mail_port || 587),
  username: item?.mail_username || '',
  password: '',
  encryption: item?.mail_encryption === 'none' ? '' : (item?.mail_encryption || ''),
  fromAddress: item?.mail_from_address || '',
  fromName: item?.mail_from_name || '',
  default: Boolean(item?.is_default || item?.active),
  isDefault: Boolean(item?.is_default),
})

const mapViewToApiPayload = (item, includePassword = true) => {
  const payload = {
    name: item?.name || '',
    mail_mailer: item?.mailer || 'smtp',
    mail_host: item?.host || '',
    mail_port: Number(item?.port || 587),
    mail_username: item?.username || '',
    mail_encryption: item?.encryption || 'none',
    mail_from_address: item?.fromAddress || '',
    mail_from_name: item?.fromName || '',
  }

  if (includePassword) {
    payload.mail_password = item?.password || ''
  }

  return payload
}

const state = {
  smtpSettings: [],
  pagination: {
    page: 1,
    perPage: 10,
    totalPages: 1,
    total: 0,
  },
  smtpOptions: [],
  optionsPagination: {
    page: 1,
    perPage: 5,
    totalPages: 1,
    total: 0,
  },
}

const mutations = {
  setSMTPSettings(state, payload) {
    state.smtpSettings = Array.isArray(payload) ? payload : []
  },

  setPagination(state, payload) {
    state.pagination = {
      page: Number(payload?.page || 1),
      perPage: Number(payload?.perPage || 10),
      totalPages: Number(payload?.totalPages || 1),
      total: Number(payload?.total || 0),
    }
  },

  setSMTPOptions(state, payload) {
    state.smtpOptions = Array.isArray(payload) ? payload : []
  },

  setOptionsPagination(state, payload) {
    state.optionsPagination = {
      page: Number(payload?.page || 1),
      perPage: Number(payload?.perPage || 5),
      totalPages: Number(payload?.totalPages || 1),
      total: Number(payload?.total || 0),
    }
  },
}

const actions = {
  async getSMTPSettings({ commit, dispatch, rootGetters }, payload = {}) {
    try {
      const page = Number(payload?.page || 1)
      const perPage = Number(payload?.perPage || 10)
      const search = payload?.search || ''

      const response = await getSMTPSettings({ page, perPage, search }, rootGetters.getToken)

      const rawData = response?.data
      const list = Array.isArray(rawData)
        ? rawData
        : rawData && typeof rawData === 'object'
          ? [rawData]
          : []

      commit('setSMTPSettings', list.map(mapApiItemToView))
      commit('setPagination', {
        page: response?.current_page || page,
        perPage: response?.per_page || perPage,
        totalPages: response?.last_page || 1,
        total: response?.total || list.length,
      })

      return response
    } catch (error) {
      commit('setSMTPSettings', [])
      commit('setPagination', { page: 1, perPage: 10, totalPages: 1, total: 0 })
      dispatch(
        'toastfy/activeErrorNotification',
        { text: error?.response?.data?.message || 'Erro ao buscar configurações SMTP.' },
        { root: true }
      )
      throw error
    }
  },

  async getSMTPSettingsOptions({ commit, dispatch, rootGetters }, payload = {}) {
    try {
      const page = Number(payload?.page || 1)
      const perPage = Number(payload?.perPage || 5)
      const search = payload?.search || ''

      const response = await getSMTPSettings({
        page,
        perPage,
        search,
      }, rootGetters.getToken)

      const rawData = response?.data
      const list = Array.isArray(rawData)
        ? rawData
        : rawData && typeof rawData === 'object'
          ? [rawData]
          : []

      commit('setSMTPOptions', list.map(mapApiItemToView))
      commit('setOptionsPagination', {
        page: response?.current_page || page,
        perPage: response?.per_page || perPage,
        totalPages: response?.last_page || 1,
        total: response?.total || list.length,
      })

      return response
    } catch (error) {
      commit('setSMTPOptions', [])
      commit('setOptionsPagination', { page: 1, perPage: 5, totalPages: 1, total: 0 })
      dispatch(
        'toastfy/activeErrorNotification',
        { text: error?.response?.data?.message || 'Erro ao buscar opções SMTP.' },
        { root: true }
      )
      throw error
    }
  },

  async createSMTPSettings({ dispatch, rootGetters }, payload) {
    try {
      return await createSMTPSettings(mapViewToApiPayload(payload), rootGetters.getToken)
    } catch (error) {
      dispatch(
        'toastfy/activeErrorNotification',
        { text: error?.response?.data?.message || 'Erro ao criar configuração SMTP.' },
        { root: true }
      )
      throw error
    }
  },

  async updateSMTPSettings({ dispatch, rootGetters }, payload) {
    try {
      if (payload?.isDefault) {
        const errorMessage = 'Não é permitido atualizar a configuração SMTP padrão.'
        dispatch('toastfy/activeErrorNotification', { text: errorMessage }, { root: true })
        throw new Error(errorMessage)
      }

      const shouldSendPassword = Boolean(payload?.password && String(payload.password).trim())
      const apiPayload = {
        id: payload?.id,
        ...mapViewToApiPayload(payload, shouldSendPassword),
      }
      return await updateSMTPSettings(apiPayload, rootGetters.getToken)
    } catch (error) {
      dispatch(
        'toastfy/activeErrorNotification',
        { text: error?.response?.data?.message || 'Erro ao atualizar configuração SMTP.' },
        { root: true }
      )
      throw error
    }
  },

  async deleteSMTPSettings({ dispatch, rootGetters }, payload) {
    try {
      if (payload?.isDefault) {
        const errorMessage = 'Não é permitido excluir a configuração SMTP padrão.'
        dispatch('toastfy/activeErrorNotification', { text: errorMessage }, { root: true })
        throw new Error(errorMessage)
      }

      return await deleteSMTPSettings(payload?.id, rootGetters.getToken)
    } catch (error) {
      dispatch(
        'toastfy/activeErrorNotification',
        { text: error?.response?.data?.message || 'Erro ao remover configuração SMTP.' },
        { root: true }
      )
      throw error
    }
  },
}

const getters = {
  getSMTPSettings: (state) => state.smtpSettings,
  getSMTPPagination: (state) => state.pagination,
  getSMTPSettingsOptions: (state) => state.smtpOptions,
  getSMTPOptionsPagination: (state) => state.optionsPagination,
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
